<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  error: {
    type: String,
    default: '',
  },
});

const columns = defineModel({
  type: Array,
  default: () => [],
});

const { t } = useI18n();

const editingColumn = ref(null);
const showAddForm = ref(false);

const defaultColors = [
  '#1f2937', // n-slate-12 (dark)
  '#dc2626', // red-600
  '#ea580c', // orange-600
  '#ca8a04', // yellow-600
  '#16a34a', // green-600
  '#0891b2', // cyan-600
  '#2563eb', // blue-600
  '#7c3aed', // violet-600
  '#c2410c', // orange-700
  '#be123c', // rose-700
];

const newColumn = ref({
  name: '',
  description: '',
  color: defaultColors[0],
  tempId: null,
});

const resetNewColumn = () => {
  newColumn.value = {
    name: '',
    description: '',
    color: defaultColors[columns.value.length % defaultColors.length],
    tempId: null,
  };
};

const addColumn = () => {
  if (newColumn.value.name.trim()) {
    columns.value.push({
      ...newColumn.value,
      position: columns.value.length,
      tempId: Date.now(),
    });
    resetNewColumn();
    showAddForm.value = false;
  }
};

const editColumn = index => {
  editingColumn.value = index;
  newColumn.value = { ...columns.value[index] };
};

const updateColumn = () => {
  if (editingColumn.value !== null && newColumn.value.name.trim()) {
    columns.value[editingColumn.value] = { ...newColumn.value };
    editingColumn.value = null;
    resetNewColumn();
  }
};

const cancelEdit = () => {
  editingColumn.value = null;
  showAddForm.value = false;
  resetNewColumn();
};

const removeColumn = index => {
  columns.value.splice(index, 1);
  // Update positions
  columns.value.forEach((column, idx) => {
    column.position = idx;
  });
};

const moveColumn = (fromIndex, toIndex) => {
  if (toIndex >= 0 && toIndex < columns.value.length) {
    const column = columns.value.splice(fromIndex, 1)[0];
    columns.value.splice(toIndex, 0, column);
    // Update positions
    columns.value.forEach((col, idx) => {
      col.position = idx;
    });
  }
};

const getColorPreview = color => {
  return {
    backgroundColor: color + '20',
    borderColor: color,
  };
};
</script>

<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ t('KANBAN.PIPELINE_MODAL.STAGES.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('KANBAN.PIPELINE_MODAL.STAGES.SUBTITLE') }}
      </p>
      <p v-if="error" class="text-sm text-red-500 mb-4">
        {{ error }}
      </p>
    </div>

    <!-- Existing Columns -->
    <div class="space-y-3">
      <div
        v-for="(column, index) in columns"
        :key="column.tempId || column.id"
        class="border border-n-weak rounded-lg p-4"
        :style="getColorPreview(column.color)"
      >
        <div v-if="editingColumn === index">
          <!-- Edit form -->
          <div class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                v-model="newColumn.name"
                :label="t('KANBAN.PIPELINE_MODAL.STAGES.NAME_LABEL')"
                :placeholder="
                  t('KANBAN.PIPELINE_MODAL.STAGES.NAME_PLACEHOLDER')
                "
                required
              />

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-2">
                  {{ t('KANBAN.PIPELINE_MODAL.STAGES.COLOR_LABEL') }}
                </label>
                <div class="flex gap-2 flex-wrap">
                  <button
                    v-for="color in defaultColors"
                    :key="color"
                    class="w-8 h-8 rounded-full border-2 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    :class="{
                      'border-n-slate-12 scale-110 ring-2 ring-blue-500 ring-offset-2':
                        newColumn.color === color,
                      'border-n-weak hover:border-n-slate-9':
                        newColumn.color !== color,
                    }"
                    :style="{ backgroundColor: color }"
                    :title="color"
                    @click="newColumn.color = color"
                  />
                </div>
              </div>
            </div>

            <TextArea
              v-model="newColumn.description"
              :label="t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_LABEL')"
              :placeholder="
                t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_PLACEHOLDER')
              "
              rows="2"
            />

            <div class="flex items-center gap-2">
              <NextButton variant="primary" size="xs" @click="updateColumn">
                {{ t('KANBAN.PIPELINE_MODAL.STAGES.SAVE') }}
              </NextButton>
              <NextButton variant="secondary" size="xs" @click="cancelEdit">
                {{ t('KANBAN.PIPELINE_MODAL.STAGES.CANCEL') }}
              </NextButton>
            </div>
          </div>
        </div>

        <div v-else class="flex items-center justify-between">
          <div class="flex-1">
            <div class="flex items-center gap-3">
              <div
                class="w-4 h-4 rounded-full border"
                :style="{
                  backgroundColor: column.color,
                  borderColor: column.color,
                }"
              />
              <div>
                <h4 class="font-medium text-n-slate-12">{{ column.name }}</h4>
                <p v-if="column.description" class="text-sm text-n-slate-11">
                  {{ column.description }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-1">
            <!-- Move buttons -->
            <NextButton
              v-if="index > 0"
              variant="ghost"
              size="xs"
              @click="moveColumn(index, index - 1)"
            >
              <i class="i-lucide-arrow-up size-4" />
            </NextButton>

            <NextButton
              v-if="index < columns.length - 1"
              variant="ghost"
              size="xs"
              @click="moveColumn(index, index + 1)"
            >
              <i class="i-lucide-arrow-down size-4" />
            </NextButton>

            <!-- Edit button -->
            <NextButton variant="ghost" size="xs" @click="editColumn(index)">
              <i class="i-lucide-pen size-4" />
            </NextButton>

            <!-- Delete button -->
            <NextButton
              variant="ghost"
              size="xs"
              class="text-red-500 hover:text-red-700"
              @click="removeColumn(index)"
            >
              <i class="i-lucide-trash-2 size-4" />
            </NextButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Add new column form -->
    <div
      v-if="showAddForm"
      class="border border-dashed border-n-weak rounded-lg p-4"
    >
      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            v-model="newColumn.name"
            :label="t('KANBAN.PIPELINE_MODAL.STAGES.NAME_LABEL')"
            :placeholder="t('KANBAN.PIPELINE_MODAL.STAGES.NAME_PLACEHOLDER')"
            required
          />

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('KANBAN.PIPELINE_MODAL.STAGES.COLOR_LABEL') }}
            </label>
            <div class="flex gap-2 flex-wrap">
              <button
                v-for="color in defaultColors"
                :key="color"
                class="w-8 h-8 rounded-full border-2 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                :class="{
                  'border-n-slate-12 scale-110 ring-2 ring-blue-500 ring-offset-2':
                    newColumn.color === color,
                  'border-n-weak hover:border-n-slate-9':
                    newColumn.color !== color,
                }"
                :style="{ backgroundColor: color }"
                :title="color"
                @click="newColumn.color = color"
              />
            </div>
          </div>
        </div>

        <TextArea
          v-model="newColumn.description"
          :label="t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_LABEL')"
          :placeholder="
            t('KANBAN.PIPELINE_MODAL.STAGES.DESCRIPTION_PLACEHOLDER')
          "
          rows="2"
        />

        <div class="flex items-center gap-2">
          <NextButton variant="primary" size="xs" @click="addColumn">
            {{ t('KANBAN.PIPELINE_MODAL.STAGES.ADD_STAGE') }}
          </NextButton>
          <NextButton variant="secondary" size="xs" @click="cancelEdit">
            {{ t('KANBAN.PIPELINE_MODAL.STAGES.CANCEL') }}
          </NextButton>
        </div>
      </div>
    </div>

    <!-- Add button -->
    <NextButton
      v-if="!showAddForm && editingColumn === null"
      variant="secondary"
      class="w-full border-dashed"
      @click="
        showAddForm = true;
        resetNewColumn();
      "
    >
      <i class="i-lucide-plus size-4" />
      {{ t('KANBAN.PIPELINE_MODAL.STAGES.ADD_STAGE') }}
    </NextButton>
  </div>
</template>
